import React, { useState, useEffect } from 'react';
import { galleryAPI } from '../services/api';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { Badge } from '../components/ui/badge';
import { Camera, X, ChevronLeft, ChevronRight } from 'lucide-react';

const GalleryPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('All');
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    galleryAPI.getAll().then(data => { setItems(data || []); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const categories = ['All', ...new Set(items.map(i => i.category).filter(Boolean))];
  const filtered = activeCategory === 'All' ? items : items.filter(i => i.category === activeCategory);

  const openLightbox = (index) => setLightbox(index);
  const closeLightbox = () => setLightbox(null);
  const nextImage = () => setLightbox(prev => (prev + 1) % filtered.length);
  const prevImage = () => setLightbox(prev => (prev - 1 + filtered.length) % filtered.length);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="gallery-page">
      <SEO
        title="Photo Gallery"
        path="/gallery"
        description="Photos from conferences, community events, awareness camps, and medical outreach programs by Dr. Venkata Pradeep Babu Koyyala."
        keywords="Dr Koyyala Photos, Oncology Conference Photos, Cancer Awareness Camp, Medical Events Gallery"
      />
      <Navigation />
      <div className="pt-32 pb-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-teal-600 to-teal-700 text-white rounded-full text-sm font-semibold mb-4 shadow-lg">
              <Camera className="w-5 h-5" />
              <span>Photo Gallery</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Moments & <span className="text-teal-700">Memories</span>
            </h1>
            <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
              Photos from conferences, community events, awareness camps, and more
            </p>
          </div>

          {/* Category filter */}
          {categories.length > 1 && (
            <div className="flex flex-wrap justify-center gap-2 mb-10">
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    activeCategory === cat
                      ? 'bg-teal-700 text-white shadow-md'
                      : 'bg-white text-gray-600 border border-gray-200 hover:border-teal-300'
                  }`}
                  data-testid={`gallery-category-${cat.toLowerCase()}`}
                >
                  {cat}
                </button>
              ))}
            </div>
          )}

          {loading ? (
            <div className="text-center py-20 text-gray-500">Loading...</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20">
              <Camera className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg mb-2">Gallery photos will appear here</p>
              <p className="text-gray-400 text-sm">Add photos from the Admin Panel</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {filtered.map((item, index) => (
                <div
                  key={item.id}
                  className="group relative aspect-square rounded-xl overflow-hidden cursor-pointer shadow-sm hover:shadow-xl transition-all"
                  onClick={() => openLightbox(index)}
                  data-testid={`gallery-item-${item.id}`}
                >
                  <img
                    src={item.image_url}
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                      <p className="font-semibold text-sm">{item.title}</p>
                      {item.category && <p className="text-xs text-gray-300">{item.category}</p>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Lightbox */}
      {lightbox !== null && filtered[lightbox] && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={closeLightbox} data-testid="gallery-lightbox">
          <button onClick={closeLightbox} className="absolute top-4 right-4 text-white hover:text-gray-300 z-10">
            <X className="w-8 h-8" />
          </button>
          {filtered.length > 1 && (
            <>
              <button onClick={(e) => { e.stopPropagation(); prevImage(); }} className="absolute left-4 text-white hover:text-gray-300 z-10">
                <ChevronLeft className="w-10 h-10" />
              </button>
              <button onClick={(e) => { e.stopPropagation(); nextImage(); }} className="absolute right-4 text-white hover:text-gray-300 z-10">
                <ChevronRight className="w-10 h-10" />
              </button>
            </>
          )}
          <div className="max-w-5xl max-h-[85vh] flex flex-col items-center" onClick={e => e.stopPropagation()}>
            <img
              src={filtered[lightbox].image_url}
              alt={filtered[lightbox].title}
              className="max-w-full max-h-[75vh] object-contain rounded-lg"
            />
            <div className="mt-4 text-center text-white">
              <p className="font-semibold">{filtered[lightbox].title}</p>
              {filtered[lightbox].description && <p className="text-sm text-gray-400 mt-1">{filtered[lightbox].description}</p>}
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default GalleryPage;
